import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, Phone, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/contact/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message marked as read');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  return (
    <AdminLayout title="Contact Messages">
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-xl p-8 text-center text-slate-500 border border-slate-100">
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-slate-500 border border-slate-100">
            No messages received yet
          </div>
        ) : (
          messages.map((message) => (
            <div 
              key={message.id} 
              className={`bg-white rounded-xl p-6 shadow-sm border ${message.read ? 'border-slate-100' : 'border-[#5ce1e6]'}`}
              data-testid={`message-${message.id}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-slate-800">{message.name}</h3>
                    {!message.read && (
                      <span className="px-2 py-1 bg-[#f0fdfd] text-[#0e7490] text-xs rounded-full">New</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                    <a href={`mailto:${message.email}`} className="flex items-center gap-1 hover:text-[#0e7490]">
                      <Mail className="w-4 h-4" />
                      {message.email}
                    </a>
                    <a href={`tel:${message.phone}`} className="flex items-center gap-1 hover:text-[#0e7490]">
                      <Phone className="w-4 h-4" />
                      {message.phone}
                    </a>
                  </div>
                  
                  <p className="text-slate-600 bg-slate-50 p-4 rounded-lg">{message.message}</p>
                  
                  <p className="text-xs text-slate-400 mt-3">
                    Received: {new Date(message.created_at).toLocaleString()}
                  </p>
                </div>
                
                {!message.read && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markAsRead(message.id)}
                    className="ml-4"
                    data-testid={`mark-read-${message.id}`}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Mark Read
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
